import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, ArrowLeft, Scale } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'grievance', label: 'Grievance' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'misconduct', label: 'Misconduct' },
  { value: 'dispute', label: 'Dispute' },
  { value: 'other', label: 'Other' },
];

export default function HrCreate() {
  const [form, setForm] = useState({ title: '', involved_parties: '', category: 'grievance', description: '' });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const canSave = form.title.trim() && form.involved_parties.trim() && form.description.trim();

  const handleSave = async () => {
    if (!canSave) {
      toast.error('Please fill all fields');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/hr/cases`, form, { headers });
      toast.success('Case created');
      navigate(`/hr/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create case');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-4" data-testid="hr-create-page">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/hr')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Scale className="h-5 w-5 text-cyan-400" /> New HR case
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">Title</Label>
              <Input value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="Short summary — e.g. Driver A vs B verbal abuse"
                     className="bg-slate-900 border-slate-700 text-white"
                     data-testid="hr-title-input" />
            </div>
            <div>
              <Label className="text-slate-300">Involved parties</Label>
              <Input value={form.involved_parties}
                     onChange={(e) => setForm({ ...form, involved_parties: e.target.value })}
                     placeholder="Names, roles — e.g. Driver A (Sipho), Driver B (John)"
                     className="bg-slate-900 border-slate-700 text-white"
                     data-testid="hr-parties-input" />
            </div>
            <div>
              <Label className="text-slate-300">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="hr-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        rows={6}
                        placeholder="What happened, when, where, and who was affected. Facts only — save the AI interpretation for later."
                        className="bg-slate-900 border-slate-700 text-white"
                        data-testid="hr-description-input" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => navigate('/hr')}
                      className="bg-slate-900 border-slate-700 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!canSave || saving}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50"
                      data-testid="hr-save-btn">
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Create case'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
